import React, { useState, useEffect, useRef } from "react";
import { Field, ErrorMessage, useFormikContext } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { currencyOptions } from "../../utils/currencyOptions";
import { minSelectableDate } from "../../utils/dateUtils";
import { groupIntegerDigits } from "../../utils/formatNumber";

// Helper function to format number with commas
const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const strValue = String(value || "").trim();
  if (strValue === "") return "";

  // Handle decimal numbers
  if (strValue.includes(".")) {
    const [integer, decimal] = strValue.split(".");
    const formattedInteger = groupIntegerDigits(integer); // ML-20: lakh/crore for INR users
    return `${formattedInteger}.${decimal}`;
  }

  return groupIntegerDigits(strValue);
};

// Helper function to parse formatted number back to numeric value
const parseFormattedNumber = (formattedValue) => {
  if (formattedValue === null || formattedValue === undefined || formattedValue === "") return "";
  const strValue = String(formattedValue || "").trim();
  if (strValue === "") return "";

  // Remove commas and return the clean numeric string
  return strValue.replace(/,/g, "");
};

// Custom Number Input Component with comma formatting
const FormattedNumberInput = ({
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  className,
  disabled,
  readOnly,
}) => {
  const [displayValue, setDisplayValue] = useState("");

  // Sync display value when prop value changes externally
  useEffect(() => {
    if (value === null || value === undefined || value === "") {
      setDisplayValue("");
    } else {
      const formatted = formatNumberWithCommas(value);
      setDisplayValue(formatted);
    }
  }, [value]);

  const handleChange = (e) => {
    const inputValue = e.target.value;

    // Allow only digits, commas, and a single decimal point
    const cleanedValue = inputValue.replace(/[^\d.,]/g, "");

    // Prevent multiple decimal points
    const parts = cleanedValue.split(".");
    let sanitizedValue = parts[0];
    if (parts.length > 1) {
      sanitizedValue += "." + parts.slice(1).join("");
    }

    // Limit to 2 decimal places
    if (sanitizedValue.includes(".")) {
      const [integer, decimal] = sanitizedValue.split(".");
      sanitizedValue = `${integer}.${decimal.slice(0, 2)}`;
    }

    setDisplayValue(sanitizedValue);

    // Parse and call onChange with the numeric value
    const numericValue = parseFormattedNumber(sanitizedValue);
    let finalValue = "";

    if (numericValue) {
      // Preserve the raw numeric string during typing to support decimals
      if (numericValue.endsWith(".") || /\.\d{0,2}$/.test(numericValue)) {
        finalValue = numericValue;
      } else {
        const num = parseInt(numericValue, 10);
        finalValue = num > 0 ? num : "";
      }
    }

    onChange({
      target: {
        name,
        value: finalValue
      }
    });
  };

  const handleBlur = (e) => {
    // Format the display value on blur
    const numericValue = parseFormattedNumber(displayValue);
    if (numericValue) {
      const num = parseFloat(numericValue);
      if (!isNaN(num)) {
        const formatted = formatNumberWithCommas(num);
        setDisplayValue(formatted);
      }
    }
    if (onBlur) onBlur(e);
  };

  return (
    <input
      ref={useRef(null)}
      type="text"
      inputMode="decimal"
      name={name}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      readOnly={readOnly}
    />
  );
};

const vehicleTypeOptions = [
  { value: "car", label: "Car" },
  { value: "bike", label: "Bike" },
  { value: "boat", label: "Boat" },
  { value: "scooter", label: "Scooter" },
  { value: "rv", label: "RV" },
];

const ownershipTypeOptions = [
  { value: "sole", label: "Sole" },
  { value: "joint", label: "Joint" },
  { value: "leased", label: "Leased" },
];

const repaymentFrequencyOptions = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "annually", label: "Annually" },
];

export const vehicleInitialValues = {
vehicleType: "",
vehicleName: "",
makeModel: "",
registrationNumber: "",
ownershipType: "",
lenderName: "",
originalLoanAmount: "",
currency: "",
outstandingAmount: "",
interestRate: "",
loanStartDate: null,
loanTerm: "",
repaymentFrequency: "",
paymentDueDate: null,
maturityDate: null,
name: "",
};

export const vehicleValidationSchema = Yup.object().shape({
  vehicleType: Yup.string().required("Vehicle type is required"),
  vehicleName: Yup.string().required("Vehicle name is required")
  .min(2, "Max length: 2 characters")
  .max(50, "Max length: 50 characters")
  .matches(
         /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
         "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
       ),
  makeModel: Yup.string().required("Make & Model is required"),
  registrationNumber: Yup.string()
    .required("Registration number is required")
    .matches(/^[A-Z]{2}-[0-9]{2}-[A-Z]{2}-[0-9]{4}$/, "Invalid registration number format. Use format like MH-12-AB-1234"),
  ownershipType: Yup.string().required("Ownership type is required"),
  lenderName: Yup.string().required("Lender name is required"),
  originalLoanAmount: Yup.number()
    .typeError("Must be a number")
    .required("Original loan amount is required")
    .positive("Must be greater than 0")
    .test(
      "is-decimal",
      "Must have up to 2 decimal places",
      (value) => {
        if (!value) return true;
        const decimalPlaces = value.toString().split(".")[1];
        return !decimalPlaces || decimalPlaces.length <= 2;
      }
    ),
  currency: Yup.string().required("Currency is required"),
  outstandingAmount: Yup.number()
    .typeError("Must be a number")
    .required("Outstanding balance is required")
    .positive("Must be greater than 0")
    .test(
      "is-decimal",
      "Must have up to 2 decimal places",
      (value) => {
        if (!value) return true;
        const decimalPlaces = value.toString().split(".")[1];
        return !decimalPlaces || decimalPlaces.length <= 2;
      }
    )
    .test(
      "outstanding-vs-loan-amount",
      "Outstanding balance cannot be greater than the loan amount.",
      function(value) {
        const { originalLoanAmount } = this.parent;
        if (!value || !originalLoanAmount) return true;
        return Number(value) <= Number(originalLoanAmount);
      }
    ),
  interestRate: Yup.number()
    .typeError("Must be a number")
    .required("Interest rate is required")
    .min(0, "Must be a positive number")
    .max(100, "Must be a number between 0 and 100")
    .test(
      "is-decimal",
      "Must have up to 2 decimal places",
      (value) => {
        if (!value) return true;
        const decimalPlaces = value.toString().split(".")[1];
        return !decimalPlaces || decimalPlaces.length <= 2;
      }
    ),
  loanStartDate: Yup.date().required("Loan start date is required").test(
    "loanStartDate-past-validation",
    "Loan start date must be today or a past date.",
    function(value) {
      if (!value) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const loanStart = new Date(value);
      loanStart.setHours(0, 0, 0, 0);
      return loanStart <= today;
    }
  ),
  loanTerm: Yup.number()
    .typeError("Must be a number")
    .required("Loan term is required")
    .positive("Must be greater than 0"),
  repaymentFrequency: Yup.string().required("Repayment frequency is required"),
  paymentDueDate: Yup.date().required("Payment due date is required").test(
    "paymentDueDate-validation",
    "Payment due date must be after the loan start date.",
    function(value) {
      const { loanStartDate } = this.parent;
      if (!value || !loanStartDate) return true;
      return new Date(value) > new Date(loanStartDate);
    }
  ),
  maturityDate: Yup.date().required("Maturity date is required").test(
    "maturityDate-start-validation",
    "Maturity date must be after the loan start date.",
    function(value) {
      const { loanStartDate } = this.parent;
      if (!value || !loanStartDate) return true;
      return new Date(value) > new Date(loanStartDate);
    }
  ),
 name: Yup.string()
       .min(3, "Min length: 3 characters")
      .max(50, "Max length: 50 characters")
      .matches(
        /^(?=.*[A-Za-z])[A-Za-z0-9 .()\-]+$/,
        "Must contain at least one letter, allowed: letters, numbers, spaces, dot, hyphen, parentheses."
      )
      .required("Name is required"),
});

const VehicleLoanForm = ({ values, setFieldValue, errors, touched, handleChange, localReadOnly }) => {
  // Blur only marks the field touched. Writing values.X back here used a stale render value and
  // erased dropdown picks on touch devices, where react-select blurs right after a selection (ML-30).
  const { setFieldTouched } = useFormikContext();
  // Helper function to check if field should show error (only when touched and has error)
  const shouldShowError = (fieldName) => {
    return errors[fieldName] && touched[fieldName];
  };
  return (
    <>
     <div className="col-md-12 mb-4">
            <label className="form-label">
              Liability Name<span className="text-danger">*</span>
            </label>
            <Field
              type="text"
              name="name"
              placeholder="e.g., Vehicle Loan, Car Loan"
              className={`form-control ${shouldShowError('name') ? "is-invalid" : ""}`}
              onChange={(e) => {
                handleChange(e);
                setFieldValue("name", e.target.value, true);
              }}
              onKeyPress={() => setFieldValue("name", values.name, true)}
              readOnly={localReadOnly}
              isDisabled={localReadOnly}
            />
            <ErrorMessage name="name" component="div" className="invalid-feedback" />
          </div>
    <div className="row">
      {/* Vehicle Type */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Vehicle Type<span className="text-danger">*</span></label>
        <Select
          options={vehicleTypeOptions}
          value={vehicleTypeOptions.find(option => option.value === values.vehicleType)}
          onChange={(option) => {
            setFieldValue("vehicleType", option.value);
            setFieldValue("vehicleType", option.value, true); // Trigger validation
          }}
          onBlur={() => setFieldTouched("vehicleType", true)}
          className="react-select-container"
          classNamePrefix="react-select"
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="vehicleType" component="div" className="text-danger small" />
      </div>

      {/* Vehicle Name */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Vehicle Name<span className="text-danger">*</span></label>
        <Field
          type="text"
          name="vehicleName"
          className={`form-control ${shouldShowError('vehicleName') ? "is-invalid" : ""}`}
          placeholder='e.g., Honda Civic, Ford F-150'
          onChange={(e) => {
            handleChange(e);
            setFieldValue("vehicleName", e.target.value, true);
          }}
          onKeyPress={() => setFieldValue("vehicleName", values.vehicleName, true)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="vehicleName" component="div" className="invalid-feedback" />
      </div>

      {/* Make & Model */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Make & Model</label>
        <Field
          type="text"
          name="makeModel"
          className={`form-control ${shouldShowError('makeModel') ? "is-invalid" : ""}`}
          placeholder ='e.g., 2020 Honda Accord, 2018 Ford Mustang'
          onChange={(e) => {
            handleChange(e);
            setFieldValue("makeModel", e.target.value, true);
          }}
          onKeyPress={() => setFieldValue("makeModel", values.makeModel, true)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="makeModel" component="div" className="invalid-feedback" />
      </div>

      {/* Registration Number */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Registration Number</label>
        <Field
          type="text"
          name="registrationNumber"
          className={`form-control ${shouldShowError('registrationNumber') ? "is-invalid" : ""}`}
          placeholder='e.g., ABC1234, XYZ5678'
          onChange={(e) => {
            handleChange(e);
            setFieldValue("registrationNumber", e.target.value, true);
          }}
          onKeyPress={() => setFieldValue("registrationNumber", values.registrationNumber, true)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="registrationNumber" component="div" className="invalid-feedback" />
      </div>

      {/* Ownership Type */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Ownership Type<span className="text-danger">*</span></label>
        <Select
          options={ownershipTypeOptions}
          value={ownershipTypeOptions.find(option => option.value === values.ownershipType)}
          onChange={(option) => {
            setFieldValue("ownershipType", option.value);
            setFieldValue("ownershipType", option.value, true); // Trigger validation
          }}
          onBlur={() => setFieldTouched("ownershipType", true)}
          className="react-select-container"
          classNamePrefix="react-select"
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="ownershipType" component="div" className="text-danger small" />
      </div>

      {/* Lender Name */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Lender Name<span className="text-danger">*</span></label>
        <Field
          type="text"
          name="lenderName"
          className={`form-control ${shouldShowError('lenderName') ? "is-invalid" : ""}`}
          placeholder='e.g., Bank of America, Chase, Local Credit Union'
          onChange={(e) => {
            handleChange(e);
            setFieldValue("lenderName", e.target.value, true);
          }}
          onKeyPress={() => setFieldValue("lenderName", values.lenderName, true)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="lenderName" component="div" className="invalid-feedback" />
      </div>

      {/* Original Loan Amount */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Original Loan Amount<span className="text-danger">*</span></label>
          <div className="input-group">
         <Select
            options={currencyOptions}
            value={currencyOptions.find(option => option.value === values.currency)}
            onChange={(option) => {
              setFieldValue("currency", option.value);
              setFieldValue("currency", option.value, true); // Trigger validation
            }}
            onBlur={() => setFieldTouched("currency", true)}
            className="currency-select"
            classNamePrefix="react-select"
            styles={{ container: base => ({ ...base, minWidth: 120 }) }}
            readOnly={localReadOnly}
            isDisabled={localReadOnly}
          />
        <FormattedNumberInput
          name="originalLoanAmount"
          value={values.originalLoanAmount}
          onChange={(e) => {
            setFieldValue("originalLoanAmount", e.target.value, true);
            // Trigger validation for dependent field
            setTimeout(() => {
              setFieldValue("outstandingAmount", values.outstandingAmount, true);
            }, 0);
          }}
          onBlur={() => setFieldTouched("originalLoanAmount", true)}
          placeholder="eg. 15,000"
          className={`form-control ${shouldShowError('originalLoanAmount') ? "is-invalid" : ""}`}
          disabled={localReadOnly}
          readOnly={localReadOnly}
        />
        </div>
           <ErrorMessage name="originalLoanAmount" component="div" className="invalid-feedback" />
        <ErrorMessage name="currency" component="div" className="invalid-feedback" />
      </div>

      {/* Outstanding Balance */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Outstanding Balance<span className="text-danger">*</span></label>
        <FormattedNumberInput
          name="outstandingAmount"
          value={values.outstandingAmount}
          onChange={(e) => setFieldValue("outstandingAmount", e.target.value, true)}
          onBlur={() => setFieldTouched("outstandingAmount", true)}
          placeholder="eg. 8,000"
          className={`form-control ${shouldShowError('outstandingAmount') ? "is-invalid" : ""}`}
          disabled={localReadOnly}
          readOnly={localReadOnly}
        />
        <ErrorMessage name="outstandingAmount" component="div" className="invalid-feedback" />
      </div>

      {/* Interest Rate */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Interest Rate (%)<span className="text-danger">*</span></label>
        <Field
          type="number"
          step="0.01"
          name="interestRate"
          className={`form-control ${shouldShowError('interestRate') ? "is-invalid" : ""}`}
          placeholder='e.g., 5.5'
          onChange={(e) => {
            handleChange(e);
            setFieldValue("interestRate", e.target.value, true);
          }}
          onKeyPress={() => setFieldValue("interestRate", values.interestRate, true)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="interestRate" component="div" className="invalid-feedback" />
      </div>

      {/* Loan Start Date - with react-datepicker */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Loan Start Date<span className="text-danger">*</span></label>
        <DatePicker
          selected={values.loanStartDate ? new Date(values.loanStartDate) : null}
          onChange={(date) => setFieldValue("loanStartDate", date, true)}
          onBlur={() => setFieldTouched("loanStartDate", true)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${shouldShowError('loanStartDate') ? "is-invalid" : ""}`}
          placeholderText="Select date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          maxDate={new Date()}
          renderCustomHeader={({
            date,
            changeMonth,
            changeYear,
          }) => {
            const today = new Date();
            const currentYear = today.getFullYear();
            const currentMonth = today.getMonth();
            const selectedYear = date && date.getFullYear ? date.getFullYear() : currentYear;

            const years = Array.from(
              { length: currentYear - 1900 + 1 },
              (_, i) => currentYear - i
            );

            const months = [
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ];

            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <select
                  value={date && date.getMonth ? date.getMonth() : currentMonth}
                  onChange={(e) =>
                    changeMonth(Number(e.target.value))
                  }
                >
                  {months.map((month, index) => {
                    const disableMonth =
                      selectedYear > currentYear ||
                      (selectedYear === currentYear &&
                        index > currentMonth);

                    return (
                      <option
                        key={month}
                        value={index}
                        disabled={disableMonth}
                      >
                        {month}
                      </option>
                    );
                  })}
                </select>

                <select
                  value={selectedYear}
                  onChange={(e) =>
                    changeYear(Number(e.target.value))
                  }
                >
                  {years.map((year) => (
                    <option
                      key={year}
                      value={year}
                      disabled={year > currentYear}
                    >
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            );
          }}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
         />
        <ErrorMessage name="loanStartDate" component="div" className="text-danger small" />
      </div>

      {/* Loan Term */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Loan Term (years)<span className="text-danger">*</span></label>
        <Field
          type="number"
          name="loanTerm"
          className={`form-control ${shouldShowError('loanTerm') ? "is-invalid" : ""}`}
          placeholder='e.g., 5'
          onChange={(e) => {
            handleChange(e);
            setFieldValue("loanTerm", e.target.value, true);
          }}
          onKeyPress={() => setFieldValue("loanTerm", values.loanTerm, true)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="loanTerm" component="div" className="invalid-feedback" />
      </div>

      {/* Repayment Frequency */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Repayment Frequency<span className="text-danger">*</span></label>
        <Select
          options={repaymentFrequencyOptions}
          value={repaymentFrequencyOptions.find(option => option.value === values.repaymentFrequency)}
          onChange={(option) => {
            setFieldValue("repaymentFrequency", option.value);
            setFieldValue("repaymentFrequency", option.value, true); // Trigger validation
          }}
          onBlur={() => setFieldTouched("repaymentFrequency", true)}
          className="react-select-container"
          classNamePrefix="react-select"
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="repaymentFrequency" component="div" className="text-danger small" />
      </div>

      {/* Payment Due Date - with react-datepicker */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Payment Due Date<span className="text-danger">*</span></label>
        <DatePicker
          selected={values.paymentDueDate ? new Date(values.paymentDueDate) : null}
          onChange={(date) => setFieldValue("paymentDueDate", date, true)}
          onBlur={() => setFieldTouched("paymentDueDate", true)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${shouldShowError('paymentDueDate') ? "is-invalid" : ""}`}
          placeholderText="Select date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          minDate={minSelectableDate(values.loanStartDate)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
         />
        <ErrorMessage name="paymentDueDate" component="div" className="text-danger small" />
      </div>

      {/* Maturity Date - with react-datepicker */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Maturity Date<span className="text-danger">*</span></label>
        <DatePicker
          selected={values.maturityDate ? new Date(values.maturityDate) : null}
          onChange={(date) => setFieldValue("maturityDate", date, true)}
          onBlur={() => setFieldTouched("maturityDate", true)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${shouldShowError('maturityDate') ? "is-invalid" : ""}`}
          placeholderText="Select date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          minDate={minSelectableDate(values.loanStartDate, values.paymentDueDate)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
         />


        <ErrorMessage name="maturityDate" component="div" className="text-danger small" />
      </div>
    </div>
    </>
  );
};

export default VehicleLoanForm;
